import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link } from "react-router-dom";
import Breadcrumb from "../components/Breadcrumb";
import NotificationDrawer from "../components/NotificationDrawer";
import { getAllNotifications } from "../lib/api";

const NotificationPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await getAllNotifications(token);
      if (response.success) {
        setNotifications(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleDrawerSuccess = () => {
    fetchNotifications();
  };

  const toggleDescription = (notificationId) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [notificationId]: !prev[notificationId]
    }));
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const paged = notifications.slice((page - 1) * perPage, page * perPage);
  const pageCount = Math.ceil(notifications.length / perPage) || 1;

  // Helper function to render recurrence schedule badges
  const renderSchedule = (item) => {
    if (!item.IsRecurring) {
      return (
        <span className="badge bg-neutral-200 text-secondary-light">
          One-Time
        </span>
      );
    }

    const daysMap = { 0: "Sun", 1: "Mon", 2: "Tue", 3: "Wed", 4: "Thu", 5: "Fri", 6: "Sat" };
    let details = "";

    if (item.RecurrenceType === "WEEKLY") {
      let daysStr = "";
      if (Array.isArray(item.RecurrenceDaysOfWeek) && item.RecurrenceDaysOfWeek.length > 0) {
        daysStr = item.RecurrenceDaysOfWeek.map((d) => daysMap[d] ?? d).join(", ");
      }
      details = `Weekly ${daysStr ? `(${daysStr})` : ""} • Every ${item.RecurrenceInterval || 1} wk`;
    } else if (item.RecurrenceType === "MONTHLY") {
      details = `Monthly (Day ${item.RecurrenceDayOfMonth || 1}) • Every ${item.RecurrenceInterval || 1} mo`;
    }

    return (
      <div>
        <div className="d-flex align-items-center gap-1 mb-1">
          <span className="badge bg-primary-100 text-primary-600 d-inline-flex align-items-center gap-1">
            <Icon icon="mdi:calendar-sync" />
            {item.RecurrenceType}
          </span>
          <span className="text-xs text-muted">
            ({item.RecurrenceCompletedCount || 0} runs)
          </span>
        </div>
        <div className="text-xs text-secondary-light fw-medium">{details}</div>
        {item.NextRunAt && (
          <div className="text-xs text-info-600 mt-1 d-flex align-items-center gap-1">
            <Icon icon="mdi:clock-outline" /> Next: {formatDate(item.NextRunAt)}
          </div>
        )}
      </div>
    );
  };

  // Function to truncate text and add read more
  const renderDescription = (description, notificationId) => {
    if (!description) return "-";
    
    const isExpanded = expandedDescriptions[notificationId] || false;
    
    if (description.length <= 100) {
      return <div className="description-cell">{description}</div>;
    }
    
    return (
      <div className="description-cell">
        <div className={`description-text ${!isExpanded ? 'collapsed' : ''}`}>
          {description}
        </div>
        <button
          type="button"
          className="read-more-btn"
          onClick={(e) => {
            e.stopPropagation();
            toggleDescription(notificationId);
          }}
        >
          {isExpanded ? "Read less" : "Read more"}
        </button>
      </div>
    );
  };

  return (
    <>
      <Breadcrumb title="Notifications" />

      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <span className="text-md fw-medium text-secondary-light mb-0">Show</span>
            <select
              className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1);
              }}
            >
              {[5, 10, 20, 50].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>

            <form
              className="navbar-search"
              onSubmit={(ev) => {
                ev.preventDefault();
                const searchTerm = ev.target.search.value.toLowerCase();
                if (searchTerm) {
                  const filtered = notifications.filter(n => 
                    n.Title?.toLowerCase().includes(searchTerm)
                  );
                  setNotifications(filtered);
                } else {
                  fetchNotifications();
                }
              }}
            >
              <input type="text" className="bg-base h-40-px w-auto" name="search" placeholder="Search notifications..." />
              <Icon icon="ion:search-outline" className="icon" />
            </form>
          </div>

          <button
            type="button"
            className="btn btn-yellow text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
            onClick={() => setShowAddDrawer(true)}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
            Add Notification
          </button>
        </div>

        <div className="card h-100 p-16 radius-12">
          <div className="table-responsive">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th scope="col">Title</th>
                  <th scope="col">Message</th>
                  <th scope="col">Sent On</th>
                  <th scope="col">Notification For</th>
                  <th scope="col">Schedule</th>
                  <th scope="col">No. of Farmers</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="7" className="text-center">Loading notifications...</td></tr>
                ) : paged.length ? (
                  paged.map((item) => (
                    <tr key={item.ID}>
                      <td className="fw-medium">{item.Title || "-"}</td>
                      <td className="description-cell-wrapper">
                        {renderDescription(item.Message, item.ID)}
                      </td>
                      <td>{formatDate(item.SentOn)}</td>
                      <td>
                        <span className="badge bg-info-100 text-info-600">
                          {item.NotificationFor}
                        </span>
                      </td>
                      <td>{renderSchedule(item)}</td>
                      <td className="text-center">{item.TotalFarmers || 0}</td>
                      <td>
                        {item.IsRecurring ? (
                          item.NextRunAt ? (
                            <span className="badge bg-info-600 text-white">Active</span>
                          ) : (
                            <span className="badge bg-success text-white">Completed</span>
                          )
                        ) : (
                          <span className={`badge ${item.IsNotificationSent ? 'bg-success' : 'bg-warning'}`}>
                            {item.IsNotificationSent ? "Sent" : "Pending"}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="7" className="text-center">No notifications found</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
            <span>
              Showing {(page - 1) * perPage + 1} to {Math.min(page * perPage, notifications.length)} of {notifications.length} entries
            </span>
            <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
              <li className="page-item">
                <Link
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md"
                  to="#"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <Icon icon="ep:d-arrow-left" />
                </Link>
              </li>
              {Array.from({ length: pageCount }).map((_, i) => (
                <li key={i} className="page-item">
                  <Link
                    className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${page === i + 1 ? "bg-primary-600 text-white" : "bg-neutral-200"}`}
                    to="#"
                    onClick={() => setPage(i + 1)}
                  >
                    {i + 1}
                  </Link>
                </li>
              ))}
              <li className="page-item">
                <Link
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md"
                  to="#"
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                >
                  <Icon icon="ep:d-arrow-right" />
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <NotificationDrawer
        isOpen={showAddDrawer}
        onClose={() => setShowAddDrawer(false)}
        onSuccess={handleDrawerSuccess}
      />
    </>
  );
};

export default NotificationPage;
